import humanize from "humanize";

function eToNumber(num) {
  let sign = "";
  num = String(num);

  // Handle negative numbers
  if (num.charAt(0) === "-") {
    sign = "-";
    num = num.substring(1);
  }

  // Split on scientific notation
  let arr = num.split(/[e]/gi);
  if (arr.length < 2) return sign + num;

  // Normalize the decimal part - handle both . and , as decimal separators
  let n = arr[0].replace(",", ".");
  const exp = parseInt(arr[1], 10);

  // Remove leading zeros but keep the one before decimal if exists
  n = n.replace(/^0+(?=\d)/, "");

  // Remove the decimal point for BigInt conversion
  let w = n.replace(".", "");

  // Calculate position for decimal point after scientific notation is applied
  const decimalIndex = n.indexOf(".");
  const pos = decimalIndex !== -1 ? decimalIndex + exp : w.length + exp;
  const L = pos - w.length;

  try {
    // Convert to BigInt after removing any remaining decimal separators
    const s = "" + BigInt(w.replace(/[.,]/g, ""));

    // Format the number based on exponent
    w =
      exp >= 0
        ? L >= 0
          ? s + "0".repeat(L)
          : r()
        : pos <= 0
          ? "0." + "0".repeat(Math.abs(pos)) + s
          : r();

    // Check for zero values
    const parts = w.split(".");
    if ((parts[0] === "0" && (!parts[1] || parts[1] === "0")) || +w === 0) {
      w = "0";
    }

    return sign + w;
  } catch (error) {
    // Fallback to original number if BigInt conversion fails
    console.warn(
      "eToNumber conversion failed, falling back to original number",
      error
    );
    return sign + num;
  }

  function r() {
    return w.replace(new RegExp(`^(.{${pos}})(.)`), "$1.$2");
  }
}

export const getOptimalDecimals = (value, percentageChange = 0.1) => {
  if (!value || value === 0) return 2;

  // Start with 0 decimals
  let decimals = 0;

  // Keep increasing decimals until the minimum increment is less than 0.1% of the value
  while (decimals < 20) {
    // Cap at 10 decimals to prevent infinite loops
    const minIncrement = 1 / 10 ** decimals;
    const percentageChange = (minIncrement / value) * 100;
    //console.log(minIncrement, percentageChange);

    if (percentageChange < 0.1) {
      break;
    }

    decimals++;
  }

  return decimals;
};

export function formatNumberWithSubscriptZeros(number, forceDecimals) {
  const numberStr = eToNumber(number);
  const leadingZerosMatch = numberStr.match(/^0\.(0+)/);
  if (!leadingZerosMatch) return numberStr;

  const leadingZerosCount = leadingZerosMatch[1].length;
  const remainingDigits = numberStr.slice(leadingZerosMatch[0].length);
  const decimals = getOptimalDecimals(number);
  const forcedPrecision =
    Math.max(forceDecimals, leadingZerosCount + 1) - leadingZerosCount;
  const precision = decimals - leadingZerosCount;

  const smallCount = String(leadingZerosCount)
    .split("")
    .map((digit) => String.fromCharCode(8320 + parseInt(digit)))
    .join("");

  return `0.0${smallCount}${remainingDigits.slice(0, Math.min(precision, forceDecimals ? forcedPrecision : precision))}`;
}

export const formatNumberHumanize = (value, options = {}) => {
  if (!value) return "0";

  if (Math.abs(value) < 0.005) {
    return formatNumberWithSubscriptZeros(value, options.decimals);
  } else if (Math.abs(value) < 100) {
    let decimals = options.decimals ?? getOptimalDecimals(value);
    return value.toFixed(decimals);
  } else if (Math.abs(value) < 1000) {
    return value.toFixed(options.decimals ?? 2);
  }

  return humanize.intword(
    value,
    ["", "k", "M", "B", "T", "Qd", "Qn", "Sx", "Sp", "Oc", "No", "De"],
    null,
    options.decimals ?? 3
  );
};
